/**
 * Utilitaires pour les projections DPE 2026
 * Calcul de l'évolution du coefficient électrique
 *
 * RÉFORME JUILLET 2024: Seuils adaptatifs pour les petites surfaces
 * Les logements de moins de 40m² bénéficient de seuils plus élevés pour corriger
 * le biais qui les pénalisait (besoins incompressibles: ECS, cuisson, etc.)
 *
 * RÉFORME JANVIER 2026: Coefficient électrique réduit de 2.3 à 1.9
 * Les DPE établis à partir du 01/01/2026 utilisent directement le coefficient 1.9
 */

// Date de changement du coefficient électrique (01/01/2026)
export const COEFFICIENT_CHANGE_DATE = '2026-01-01';

export interface DPEProjection {
  // ÉNERGIE FINALE (source de vérité, invariante)
  energieFinale: number; // Consommation réelle par m² en kWh/m²

  // SCENARIO MALUS 2.3 (ancienne méthode, avant 2026)
  coefficientActuel: number; // Toujours 2.3
  energiePrimaireActuelle: number; // EF × 2.3
  consoPar_m2_Actuel: number; // EP par m² avec malus 2.3
  etiquetteActuelle: string; // Étiquette DPE calculée

  // SCENARIO MALUS 1.9 (méthode 2026)
  coefficientFutur2026: number; // Toujours 1.9
  energiePrimaire2026: number; // EF × 1.9
  consoPar_m2_2026: number; // EP par m² avec malus 1.9
  etiquette2026: string; // Étiquette DPE calculée

  // SCENARIO MALUS 1.3 (projection à terme, information)
  coefficientFuturTerme: number; // Toujours 1.3
  energiePrimaireTerme: number; // EF × 1.3
  consoPar_m2_Terme: number; // EP par m² avec malus 1.3
  etiquetteTerme: string; // Étiquette DPE calculée

  // Gains (calculés par rapport au scénario malus 2.3)
  gainEnergiePrimaire2026: number; // EP malus 2.3 - EP malus 1.9
  gainPourcentage2026: number; // Pourcentage de gain
  gainEnergiePrimaireTerme: number; // EP malus 2.3 - EP malus 1.3
  gainPourcentageTerme: number; // Pourcentage de gain

  // Amélioration de classe DPE
  classImprovement2026: boolean; // true si étiquette s'améliore avec malus 1.9
  classImprovementTerme: boolean; // true si étiquette s'améliore avec malus 1.3

  // Métadonnées de date
  isDpePre2026: boolean;
  dpeEstablishmentDate: string | null;

  // Installation mixte (chauffage non-élec + ECS élec)
  isMixedInstallation?: boolean;
  mixedInstallationDetails?: {
    consoElectricEF: number;
    consoNonElectricEF: number;
    epElectricActuel: number;
    epNonElectric: number;
  };
}

/**
 * Détermine si un DPE a été établi avant le 01/01/2026
 *
 * @param dateEtablissementDpe Date d'établissement du DPE (format ISO ou YYYY-MM-DD)
 * @returns true si le DPE date d'avant le 01/01/2026, false sinon
 *
 * Note: Les DPE du 01/01/2026 et après sont considérés comme post-2026 car établis
 * avec les nouvelles règles (coefficient 1.9 au lieu de 2.3)
 */
export function isDPEPre2026(dateEtablissementDpe: string | null | undefined): boolean {
  if (!dateEtablissementDpe) {
    // Par défaut, si pas de date, on considère comme pré-2026 par sécurité
    console.log('⚠️ DPE date missing, considering as pre-2026 by default');
    return true;
  }

  try {
    const dpeDate = new Date(dateEtablissementDpe);
    const changeDate = new Date(COEFFICIENT_CHANGE_DATE);

    // Vérifier que la date est valide
    if (isNaN(dpeDate.getTime())) {
      console.log('⚠️ Invalid DPE date format, considering as pre-2026 by default');
      return true;
    }

    const isPre2026 = dpeDate < changeDate;

    console.log('🔍 DPE date check:', {
      dpeDate: dateEtablissementDpe,
      changeDate: COEFFICIENT_CHANGE_DATE,
      isPre2026
    });

    return isPre2026;
  } catch (error) {
    console.error('Error parsing DPE date:', error);
    return true; // Par défaut, considérer comme pré-2026
  }
}

/**
 * Détecte si le logement utilise un chauffage électrique
 */
export function isChauffageElectriqueDetected(dpeData: any): boolean {
  if (!dpeData) return false;

  const criteres = [
    // Critère 1: Type générateur contient "électrique"
    dpeData.type_generateur_chauffage_principal?.toLowerCase().includes('électrique'),

    // Critère 2: Type générateur contient "radiateur électrique"
    dpeData.type_generateur_chauffage_principal?.toLowerCase().includes('radiateur électrique'),

    // Critère 3: Type énergie principale est électricité
    dpeData.type_energie_principale_chauffage === 'Électricité',

    // Critère 4: Description générateur contient électrique
    dpeData.description_generateur_chauffage_n1_installation_n1?.toLowerCase().includes('electrique'),

    // Critère 5: Vérification coefficient (sécurité)
    dpeData.conso_5_usages_ep && dpeData.conso_5_usages_ef &&
    (dpeData.conso_5_usages_ep / dpeData.conso_5_usages_ef) > 2.0 &&
    (dpeData.conso_5_usages_ep / dpeData.conso_5_usages_ef) < 2.5
  ];

  const isElectric = criteres.some(critere => critere === true);

  console.log('🔍 Electric heating detection:', {
    type_generateur: dpeData.type_generateur_chauffage_principal,
    type_energie: dpeData.type_energie_principale_chauffage,
    coefficient_detected: dpeData.conso_5_usages_ep && dpeData.conso_5_usages_ef ?
      (dpeData.conso_5_usages_ep / dpeData.conso_5_usages_ef).toFixed(2) : 'N/A',
    is_electric: isElectric
  });

  return isElectric;
}

/**
 * Détecte si l'ECS (eau chaude sanitaire) utilise l'électricité
 */
export function isECSElectriqueDetected(dpeData: any): boolean {
  if (!dpeData) return false;

  const typeEnergie = dpeData.type_energie_principale_ecs?.toLowerCase() || '';
  const typeGenerateur = dpeData.type_generateur_chauffage_principal_ecs?.toLowerCase() || '';

  const isElectricECS =
    typeEnergie.includes('électricité') ||
    typeEnergie.includes('electricite') ||
    typeGenerateur.includes('électrique') ||
    typeGenerateur.includes('electrique') ||
    typeGenerateur.includes('ballon') && typeEnergie.includes('électr');

  console.log('🔍 Electric ECS detection:', {
    type_energie_ecs: dpeData.type_energie_principale_ecs,
    type_generateur_ecs: dpeData.type_generateur_chauffage_principal_ecs,
    is_electric_ecs: isElectricECS
  });

  return isElectricECS;
}

/**
 * Détecte si l'installation est mixte (chauffage non-électrique + ECS électrique)
 */
export function detectMixedElectricInstallation(dpeData: any): boolean {
  const isChauffageElec = isChauffageElectriqueDetected(dpeData);
  const isECSElec = isECSElectriqueDetected(dpeData);

  const isMixed = !isChauffageElec && isECSElec;

  console.log('🔍 Mixed installation detection:', {
    chauffage_electrique: isChauffageElec,
    ecs_electrique: isECSElec,
    is_mixed: isMixed
  });

  return isMixed;
}

/**
 * Retourne les seuils DPE adaptés selon la surface du logement
 * Réforme juillet 2024: seuils progressifs pour petites surfaces (< 40m²)
 *
 * @param surface Surface habitable en m²
 * @returns Tableau des seuils [A, B, C, D, E, F] en kWh/m²/an
 *
 * Exemples:
 * - 15m² : [99, 139, 262, 332, 420, 513] -> 473 kWh/m²/an = F (au lieu de G avec seuils standards)
 * - 50m² : [70, 110, 180, 250, 330, 420] -> seuils standards
 */
export function getDPEThresholds(surface: number): number[] {
  if (surface <= 10) {
    return [123, 163, 328, 427, 532, 639];
  } else if (surface <= 20) {
    return [99, 139, 262, 332, 420, 513];
  } else if (surface <= 25) {
    return [87, 127, 229, 299, 384, 475];
  } else if (surface <= 30) {
    return [80, 120, 209, 279, 362, 453];
  } else if (surface <= 35) {
    return [75, 115, 196, 266, 348, 438];
  } else if (surface <= 40) {
    return [72, 112, 187, 257, 337, 427];
  } else if (surface <= 45) {
    return [69, 109, 179, 249, 329, 419];
  } else {
    return [70, 110, 180, 250, 330, 420];
  }
}

/**
 * Calcule l'étiquette DPE selon la consommation par m²
 *
 * @param consommationParM2 Consommation en kWh/m²/an
 * @param surfaceHabitable Surface habitable en m² (optionnel)
 * @returns Classe énergétique de A à G
 *
 * Si la surface est fournie et < 40m², utilise les seuils adaptatifs de la réforme juillet 2024.
 * Sinon, utilise les seuils standards.
 */
export function calculateEtiquetteDPE(consommationParM2: number, surfaceHabitable?: number): string {
  const thresholds = surfaceHabitable ? getDPEThresholds(surfaceHabitable) : [70, 110, 180, 250, 330, 420];

  if (consommationParM2 <= thresholds[0]) return 'A';
  if (consommationParM2 <= thresholds[1]) return 'B';
  if (consommationParM2 <= thresholds[2]) return 'C';
  if (consommationParM2 <= thresholds[3]) return 'D';
  if (consommationParM2 <= thresholds[4]) return 'E';
  if (consommationParM2 <= thresholds[5]) return 'F';
  return 'G';
}

/**
 * Calcule la classe énergétique PURE (basée uniquement sur la consommation)
 * Ignore le critère GES pour afficher la vraie classe énergétique
 */
export function calculateClasseEnergetiquePure(
  consommationParM2: number,
  surfaceHabitable?: number
): string {
  return calculateEtiquetteDPE(consommationParM2, surfaceHabitable);
}

/**
 * Calcule la classe climat (basée uniquement sur les émissions GES)
 */
export function calculateClasseClimat(emissionsGESParM2: number): string {
  const thresholds = [6, 11, 30, 50, 70, 100];

  if (emissionsGESParM2 <= thresholds[0]) return 'A';
  if (emissionsGESParM2 <= thresholds[1]) return 'B';
  if (emissionsGESParM2 <= thresholds[2]) return 'C';
  if (emissionsGESParM2 <= thresholds[3]) return 'D';
  if (emissionsGESParM2 <= thresholds[4]) return 'E';
  if (emissionsGESParM2 <= thresholds[5]) return 'F';
  return 'G';
}

/**
 * Détermine la classe DPE finale (la pire des deux: énergie ou climat)
 * C'est cette classe qui apparaît en gros sur le DPE officiel
 */
export function getClasseFinaleOfficielle(
  classeEnergie: string,
  classeClimat: string
): string {
  const ordre = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
  const indexEnergie = ordre.indexOf(classeEnergie);
  const indexClimat = ordre.indexOf(classeClimat);

  return indexEnergie > indexClimat ? classeEnergie : classeClimat;
}

/**
 * Calcule la projection DPE 2026 pour chauffage électrique
 *
 * IMPORTANT: Cette fonction calcule l'évolution AUTOMATIQUE du DPE liée au changement
 * de coefficient électrique réglementaire (2.3 -> 1.9 en 2026).
 *
 * MÉTHODE DE CALCUL CORRIGÉE (Mars 2026):
 *
 * PRINCIPE: Toujours partir de l'ÉNERGIE FINALE (consommation réelle du logement)
 * L'énergie finale est invariante. Seul le malus change (2.3 → 1.9 → 1.3).
 *
 * CAS 1 - Tout électrique (chauffage + ECS électrique):
 * 1. Partir de conso_5_usages_ef (énergie finale totale, valeur réelle)
 * 2. Calculer EF par m² = conso_5_usages_ef / surface
 * 3. Calculer EP avec différents malus: EF × 2.3, EF × 1.9, EF × 1.3
 *
 * CAS 2 - Installation mixte (chauffage non-élec + ECS électrique):
 * 1. Séparer les consommations électriques (ECS + éclairage + auxiliaires) des non-électriques (chauffage)
 * 2. Appliquer coefficient 1.9/1.3 sur la partie électrique uniquement
 * 3. Appliquer coefficient 1.0 (inchangé) sur la partie non-électrique (fuel/gaz/bois)
 * 4. Sommer les deux pour obtenir la projection 2026
 *
 * Exemple tout électrique (DPE 2586E1218145F):
 * - EF réelle: 168 kWh/m² (invariante, source de vérité)
 * - EP avec malus 2.3: 168 × 2.3 = 386 kWh/m² (ancienne note)
 * - EP avec malus 1.9: 168 × 1.9 = 319 kWh/m² (nouvelle note 2026)
 * - EP avec malus 1.3: 168 × 1.3 = 218 kWh/m² (projection à terme)
 *
 * Cette projection ne doit PAS être dans le simulateur de travaux - elle est affichée séparément.
 */
export function calculateDPEProjection(dpeData: any): DPEProjection | null {
  console.log('🔍 DEBUG_DPE_PROJECTION: Input dpeData:', {
    conso_5_usages_ef: dpeData?.conso_5_usages_ef,
    conso_5_usages_par_m2_ef: dpeData?.conso_5_usages_par_m2_ef,
    conso_5_usages_par_m2_ep: dpeData?.conso_5_usages_par_m2_ep,
    primary_energy_consumption: dpeData?.primary_energy_consumption,
    surface_habitable_logement: dpeData?.surface_habitable_logement,
    living_area: dpeData?.living_area,
    type_energie_principale_chauffage: dpeData?.type_energie_principale_chauffage,
    type_energie_principale_ecs: dpeData?.type_energie_principale_ecs,
    date_etablissement_dpe: dpeData?.date_etablissement_dpe
  });

  const isChauffageElec = isChauffageElectriqueDetected(dpeData);
  const isECSElec = isECSElectriqueDetected(dpeData);
  const isMixed = detectMixedElectricInstallation(dpeData);

  // Si aucune installation électrique, pas de projection 2026
  if (!isChauffageElec && !isECSElec) {
    console.log('🔍 DEBUG_DPE_PROJECTION: No electric installation detected');
    return null;
  }

  console.log('🔍 DEBUG_DPE_PROJECTION: Electric installation detected', {
    chauffage_elec: isChauffageElec,
    ecs_elec: isECSElec,
    is_mixed: isMixed
  });

  // Récupérer et analyser la date d'établissement du DPE
  const dateEtablissementDpe = dpeData.date_etablissement_dpe || null;
  const isPre2026 = isDPEPre2026(dateEtablissementDpe);

  const coefficientMalus23 = 2.3;
  const coefficientMalus19 = 1.9;
  const coefficientMalus13 = 1.3;
  const coefficientNonElectrique = 1.0;

  // Extraire la surface habitable pour appliquer les seuils adaptatifs (réforme juillet 2024)
  const surfaceHabitable = parseFloat(dpeData.living_area) ||
                          parseFloat(dpeData.surface_habitable_logement) ||
                          null;

  if (!surfaceHabitable || surfaceHabitable <= 0) {
    console.log('🔍 DEBUG_DPE_PROJECTION: Missing or invalid surface habitable');
    return null;
  }

  console.log('🔍 DEBUG_DPE_PROJECTION: Surface habitable:', surfaceHabitable);

  // MÉTHODE CORRIGÉE: Partir de l'ÉNERGIE FINALE (source de vérité)
  // L'EP du DPE peut avoir été calculée avec n'importe quel malus (2.3, 1.9, etc.)
  const consoPar_m2_EP_DPE = parseFloat(dpeData.conso_5_usages_par_m2_ep) ||
                              parseFloat(dpeData.primary_energy_consumption) || 0;

  let consoPar_m2_EF: number;
  let consoPar_m2_EP_Malus23: number;
  let consoPar_m2_EP_Malus19: number;
  let consoPar_m2_EP_Malus13: number;
  let mixedInstallationDetails: any = undefined;

  // CAS 1: Chauffage électrique (avec ou sans ECS électrique)
  // La condition est isChauffageElec SEUL : quand le chauffage est électrique, il représente
  // l'essentiel de la consommation. Le coefficient doit s'appliquer à l'ensemble des 5 usages
  // via conso_5_usages_ef. Les cas où type_energie_principale_ecs = "Non affecté" (ECS absente
  // du DPE, ex: appartement sans ECS propre) tombaient incorrectement en CAS 3, appliquant
  // le coefficient 1.0 au chauffage → valeur erronée (ex: 191 au lieu de 346 kWh/m²/an).
  if (isChauffageElec) {
    console.log('🔍 DEBUG_DPE_PROJECTION: Case 1 - Electric heating (isECSElec:', isECSElec, ')');

    // Méthode 1: Utiliser conso_5_usages_ef directement (PRIORITÉ)
    const conso_5_usages_ef = parseFloat(dpeData.conso_5_usages_ef) || 0;

    if (conso_5_usages_ef > 0) {
      // SOURCE DE VÉRITÉ: Énergie finale totale du DPE
      consoPar_m2_EF = conso_5_usages_ef / surfaceHabitable;
      console.log('🔍 DEBUG_DPE_PROJECTION: Using conso_5_usages_ef (best method)');
    } else {
      // Méthode 2: Utiliser conso_5_usages_par_m2_ef si disponible
      const conso_5_usages_par_m2_ef = parseFloat(dpeData.conso_5_usages_par_m2_ef) || 0;

      if (conso_5_usages_par_m2_ef > 0) {
        consoPar_m2_EF = conso_5_usages_par_m2_ef;
        console.log('🔍 DEBUG_DPE_PROJECTION: Using conso_5_usages_par_m2_ef (fallback 1)');
      } else {
        // Méthode 3: Calculer à partir des consommations par usage (fallback)
        const consoEF = (parseFloat(dpeData.conso_chauffage_ef) || 0) +
                       (parseFloat(dpeData.conso_ecs_ef) || 0) +
                       (parseFloat(dpeData.conso_eclairage_ef) || 0) +
                       (parseFloat(dpeData.conso_auxiliaires_ef) || 0) +
                       (parseFloat(dpeData.conso_refroidissement_ef) || 0);

        if (consoEF > 0) {
          consoPar_m2_EF = consoEF / surfaceHabitable;
          console.log('🔍 DEBUG_DPE_PROJECTION: Calculated from individual EF consumptions (fallback 2)');
        } else {
          // Méthode 4: En dernier recours, détecter le coefficient à partir de l'EP
          console.log('🔍 DEBUG_DPE_PROJECTION: WARNING - No EF data available, using coefficient detection (least reliable)');

          const ratio = consoPar_m2_EP_DPE / (parseFloat(dpeData.conso_5_usages_ef) || 1);
          let detectedCoefficient = coefficientMalus23; // Par défaut

          if (ratio >= 1.8 && ratio <= 2.0) {
            detectedCoefficient = coefficientMalus19;
          } else if (ratio >= 2.0 && ratio <= 2.5) {
            detectedCoefficient = coefficientMalus23;
          }

          consoPar_m2_EF = consoPar_m2_EP_DPE / detectedCoefficient;
          console.log('🔍 DEBUG_DPE_PROJECTION: Detected coefficient:', detectedCoefficient);
        }
      }
    }

    if (!consoPar_m2_EF || consoPar_m2_EF <= 0) {
      console.log('🔍 DEBUG_DPE_PROJECTION: Could not determine final energy consumption');
      return null;
    }

    // Calculer les projections avec les différents malus
    // IMPORTANT: On recalcule TOUTES les EP à partir de l'EF
    consoPar_m2_EP_Malus23 = consoPar_m2_EF * coefficientMalus23;
    consoPar_m2_EP_Malus19 = consoPar_m2_EF * coefficientMalus19;
    consoPar_m2_EP_Malus13 = consoPar_m2_EF * coefficientMalus13;

  // CAS 2: Installation mixte (chauffage non-élec + ECS électrique)
  } else if (isMixed) {
    console.log('🔍 DEBUG_DPE_PROJECTION: Case 2 - Mixed installation');

    // Récupérer les consommations détaillées en énergie finale
    const consoNonElecEF = parseFloat(dpeData.conso_chauffage_ef) || 0;
    const consoECSEF = parseFloat(dpeData.conso_ecs_ef) || 0;
    const consoEclairageEF = parseFloat(dpeData.conso_eclairage_ef) || 0;
    const consoAuxiliairesEF = parseFloat(dpeData.conso_auxiliaires_ef) || 0;

    // Part électrique = ECS + éclairage + auxiliaires
    const consoElecEF = consoECSEF + consoEclairageEF + consoAuxiliairesEF;

    // Calculer l'énergie finale totale par m²
    consoPar_m2_EF = (consoNonElecEF + consoElecEF) / surfaceHabitable;

    // Calculer les énergies primaires avec différents malus
    const epNonElec = consoNonElecEF * coefficientNonElectrique;
    const epElecMalus23 = consoElecEF * coefficientMalus23;
    const epElecMalus19 = consoElecEF * coefficientMalus19;
    const epElecMalus13 = consoElecEF * coefficientMalus13;

    // Totaux
    const energiePrimaireMalus23 = epNonElec + epElecMalus23;
    const energiePrimaireMalus19 = epNonElec + epElecMalus19;
    const energiePrimaireMalus13 = epNonElec + epElecMalus13;

    // Convertir en par m²
    consoPar_m2_EP_Malus23 = energiePrimaireMalus23 / surfaceHabitable;
    consoPar_m2_EP_Malus19 = energiePrimaireMalus19 / surfaceHabitable;
    consoPar_m2_EP_Malus13 = energiePrimaireMalus13 / surfaceHabitable;

    // Sauvegarder les détails pour l'affichage
    mixedInstallationDetails = {
      consoElectricEF: consoElecEF,
      consoNonElectricEF: consoNonElecEF,
      epElectricActuel: epElecMalus23,
      epNonElectric: epNonElec
    };

    console.log('🔍 DEBUG_DPE_PROJECTION: Mixed installation details:', {
      consoNonElecEF,
      consoElecEF,
      consoPar_m2_EF,
      epNonElec,
      epElecMalus23,
      epElecMalus19,
      energiePrimaireMalus19,
      consoPar_m2_EP_Malus19
    });

  // CAS 3: ECS électrique uniquement (sans chauffage électrique)
  } else {
    console.log('🔍 DEBUG_DPE_PROJECTION: Case 3 - ECS electric only');

    // Même logique que mixte
    const consoNonElecEF = parseFloat(dpeData.conso_chauffage_ef) || 0;
    const consoECSEF = parseFloat(dpeData.conso_ecs_ef) || 0;
    const consoEclairageEF = parseFloat(dpeData.conso_eclairage_ef) || 0;
    const consoAuxiliairesEF = parseFloat(dpeData.conso_auxiliaires_ef) || 0;

    const consoElecEF = consoECSEF + consoEclairageEF + consoAuxiliairesEF;

    // Calculer l'énergie finale totale par m²
    consoPar_m2_EF = (consoNonElecEF + consoElecEF) / surfaceHabitable;

    // Calculer les énergies primaires avec différents malus
    const epNonElec = consoNonElecEF * coefficientNonElectrique;
    const epElecMalus23 = consoElecEF * coefficientMalus23;
    const epElecMalus19 = consoElecEF * coefficientMalus19;
    const epElecMalus13 = consoElecEF * coefficientMalus13;

    const energiePrimaireMalus23 = epNonElec + epElecMalus23;
    const energiePrimaireMalus19 = epNonElec + epElecMalus19;
    const energiePrimaireMalus13 = epNonElec + epElecMalus13;

    consoPar_m2_EP_Malus23 = energiePrimaireMalus23 / surfaceHabitable;
    consoPar_m2_EP_Malus19 = energiePrimaireMalus19 / surfaceHabitable;
    consoPar_m2_EP_Malus13 = energiePrimaireMalus13 / surfaceHabitable;

    mixedInstallationDetails = {
      consoElectricEF: consoElecEF,
      consoNonElectricEF: consoNonElecEF,
      epElectricActuel: epElecMalus23,
      epNonElectric: epNonElec
    };
  }

  console.log('🔍 DEBUG_DPE_PROJECTION: Calculations:', {
    consoPar_m2_EF,
    consoPar_m2_EP_DPE,
    consoPar_m2_EP_Malus23,
    consoPar_m2_EP_Malus19,
    consoPar_m2_EP_Malus13,
    gain_23_to_19: consoPar_m2_EP_Malus23 - consoPar_m2_EP_Malus19,
    gain_23_to_13: consoPar_m2_EP_Malus23 - consoPar_m2_EP_Malus13
  });

  // Calculer les étiquettes avec les seuils adaptatifs si surface < 40m²
  const etiquetteMalus23 = calculateEtiquetteDPE(consoPar_m2_EP_Malus23, surfaceHabitable || undefined);
  const etiquetteMalus19 = calculateEtiquetteDPE(consoPar_m2_EP_Malus19, surfaceHabitable || undefined);
  const etiquetteMalus13 = calculateEtiquetteDPE(consoPar_m2_EP_Malus13, surfaceHabitable || undefined);

  // Calculer les gains par rapport au malus 2.3 (ancien système)
  const gainEnergiePrimaire2026 = consoPar_m2_EP_Malus23 - consoPar_m2_EP_Malus19;
  const gainPourcentage2026 = ((consoPar_m2_EP_Malus23 - consoPar_m2_EP_Malus19) / consoPar_m2_EP_Malus23) * 100;
  const gainEnergiePrimaireTerme = consoPar_m2_EP_Malus23 - consoPar_m2_EP_Malus13;
  const gainPourcentageTerme = ((consoPar_m2_EP_Malus23 - consoPar_m2_EP_Malus13) / consoPar_m2_EP_Malus23) * 100;

  console.log('🔍 DEBUG_DPE_PROJECTION: Final results:', {
    gainEnergiePrimaire2026,
    gainPourcentage2026,
    etiquetteMalus23,
    etiquetteMalus19,
    classImprovement: etiquetteMalus19 < etiquetteMalus23,
    isPre2026,
    dateEtablissementDpe,
    isMixed
  });

  return {
    energieFinale: consoPar_m2_EF,
    coefficientActuel: coefficientMalus23,
    energiePrimaireActuelle: consoPar_m2_EP_Malus23,
    consoPar_m2_Actuel: consoPar_m2_EP_Malus23,
    etiquetteActuelle: etiquetteMalus23,

    coefficientFutur2026: coefficientMalus19,
    energiePrimaire2026: consoPar_m2_EP_Malus19,
    consoPar_m2_2026: consoPar_m2_EP_Malus19,
    etiquette2026: etiquetteMalus19,

    coefficientFuturTerme: coefficientMalus13,
    energiePrimaireTerme: consoPar_m2_EP_Malus13,
    consoPar_m2_Terme: consoPar_m2_EP_Malus13,
    etiquetteTerme: etiquetteMalus13,

    gainEnergiePrimaire2026,
    gainPourcentage2026,
    gainEnergiePrimaireTerme,
    gainPourcentageTerme,

    classImprovement2026: etiquetteMalus19 < etiquetteMalus23,
    classImprovementTerme: etiquetteMalus13 < etiquetteMalus23,

    isDpePre2026: isPre2026,
    dpeEstablishmentDate: dateEtablissementDpe,

    isMixedInstallation: isMixed,
    mixedInstallationDetails
  };
}

/**
 * Obtient la couleur CSS pour une étiquette DPE
 */
export function getEtiquetteColor(etiquette: string): string {
  switch (etiquette) {
    case 'A': return 'green-600';
    case 'B': return 'green-500';
    case 'C': return 'yellow-500';
    case 'D': return 'orange-500';
    case 'E': return 'red-500';
    case 'F': return 'red-600';
    case 'G': return 'red-700';
    default: return 'gray-500';
  }
}

/**
 * Compare two DPE energy classes to determine if one is better than the other
 * Returns true if class1 is better (lower letter) than class2
 */
export function isEnergyClassBetter(class1: string, class2: string): boolean {
  const classOrder = { 'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 6, 'G': 7 };
  
  const value1 = classOrder[class1.toUpperCase()] || 7;
  const value2 = classOrder[class2.toUpperCase()] || 7;
  
  return value1 < value2;
}